// services/authService.ts
import { apiClient, API_BASE_URL } from '../config/api';
import { ENDPOINTS } from '../config/endpoints';
import { LoginResponse, User } from '../types/api';

export class AuthService {
  static async login(email: string, password: string): Promise<User> {
  const endpoint = ENDPOINTS.AUTH.LOGIN;
  // O backend espera o campo 'senha' em vez de 'password'
  const payload = { email, senha: password };
  // Mostrar no DevTools o endpoint chamado e o JSON enviado (somente em dev)
  // (útil para inspecionar requisições durante desenvolvimento)
  const __isDev = process.env.NODE_ENV === 'development';
  if (__isDev) {
    // eslint-disable-next-line no-console
    console.log(`REQUEST -> ${endpoint}`, JSON.stringify(payload));
  }

    // Use apiClient to perform the request; apiClient centraliza logs in dev
    let parsed: any = null;
    try {
      parsed = await apiClient.request<any>(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch (err: any) {
      // apiClient already throws with a helpful message; rethrow
      throw err;
    }

    // Normalizar formatos de resposta possíveis
    const success = !!(parsed && (parsed.success === true || parsed.data || parsed.token));
    const tokenVal = parsed?.data?.token ?? parsed?.token ?? null;
    const userVal = parsed?.data?.user ?? parsed?.user ?? null;

    if (success) {
      if (tokenVal) {
        localStorage.setItem('authToken', tokenVal);
      }
      if (userVal) {
        // Normalize user object: ensure top-level empresaId and grupoId exist
        const inferredEmpresaId = userVal.empresaId || userVal.accessScope?.empresaId || (userVal.permissions && Array.isArray(userVal.permissions.empresaIds) && userVal.permissions.empresaIds[0]) || null;
        const inferredGrupoId = userVal.grupoId || userVal.accessScope?.grupoId || (userVal.permissions && Array.isArray(userVal.permissions.grupoIds) && userVal.permissions.grupoIds[0]) || null;

        const normalizedUser = {
          ...userVal,
          empresaId: inferredEmpresaId,
          grupoId: inferredGrupoId,
          // if backend provided the names (preferred), keep them to avoid extra fetch
          empresaNome: userVal.empresa?.nome || userVal.empresaNome || null,
          grupoNome: userVal.grupo?.nome || userVal.grupoNome || null,
        } as any;

        localStorage.setItem('user', JSON.stringify(normalizedUser));
        // Dispatch a global event so other parts of the app (Navbar, hooks) can react
        try {
          window.dispatchEvent(new CustomEvent('auth:change', { detail: normalizedUser }));
        } catch (e) {
          console.warn('Could not dispatch auth change event', e);
        }
        return normalizedUser as User;
      }

      // Se não vier usuário, ainda assim retornar currentUser do localStorage (se houver)
      const current = this.getCurrentUser();
      if (current) return current;

      throw new Error('Login bem-sucedido, mas usuário não retornado pelo servidor');
    }

    const message = parsed?.message || parsed?.error || 'Erro no login';
    throw new Error(message || 'Erro no login');
  }

  static async loginMaster(date: Date): Promise<User> {
    // Gerar senha diária: aDDbMMcYY
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    const password = `a${day}b${month}c${year}`;

    return this.login('master', password);
  }

  static async logout(): Promise<void> {
    try {
  await apiClient.request(ENDPOINTS.AUTH.LOGOUT, { method: 'POST' });
    } catch (error) {
      // Mesmo se der erro na API, limpar os dados locais
      console.warn('Erro no logout da API:', error);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      try {
        window.dispatchEvent(new CustomEvent('auth:change', { detail: null }));
      } catch (e) {
        console.warn('Could not dispatch auth change event (logout)', e);
      }
      window.location.href = '/login';
    }
  }

  static getCurrentUser(): User | null {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Erro ao recuperar usuário do localStorage:', error);
      localStorage.removeItem('user');
      return null;
    }
  }

  static isAuthenticated(): boolean {
    const token = localStorage.getItem('authToken');
    const user = this.getCurrentUser();
    return !!(token && user);
  }

  static isMaster(): boolean {
    const user = this.getCurrentUser();
    return user?.type === 'master';
  }

  static isGrupo(): boolean {
    const user = this.getCurrentUser();
    return user?.type === 'grupo';
  }

  static isEmpresa(): boolean {
    const user = this.getCurrentUser();
    return user?.type === 'empresa';
  }

  static getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  static canManageEmpresas(): boolean {
    return this.isMaster() || this.isGrupo();
  }

  static getEmpresaId(): string | undefined {
    const user = this.getCurrentUser();
    return user?.empresaId;
  }

  static getGrupoId(): string | undefined {
    const user = this.getCurrentUser();
    return user?.grupoId;
  }
}